/**
 * version2026: "เพิ่ม Icon สถานะ" status-icon catalogue.
 *
 * The selection itself is NOT here — it lives on the conversation row
 * (tb_conversation.status_icons, written by POST /conversations/:id/status-icons). This module owns
 * only the *catalogue*: the ids, the Thai copy and the glyph/colour each id resolves to. Both
 * consumers (StatusIconPickerV2026 and ChatCardV2026) read it from here.
 *
 * ⚠️ THE ID LIST EXISTS TWICE. The server validates writes against its own copy in
 * chat-server/helpers/statusIconHelper.js (ids only, nothing else — labels and glyphs are
 * client-only). Adding or renaming an id means editing BOTH files. A catalogue endpoint would
 * collapse the two; it was deliberately cut from this round.
 *
 * THE THAI COPY IS CONFIRMED (reviewed 2026-08-14). It was transcribed from the mockup, shown to the
 * user alongside shorter alternatives, and DELIBERATELY KEPT VERBATIM — including the long first
 * label. Do not "tidy" or shorten any of the five. Four of them wrap to two lines in the 320px
 * picker card, which is the designed-for state (StatusIconPickerV2026 pins line-height: 20px so a
 * wrapped row is a deterministic 60px). This file is still the ONLY place the strings exist, so a
 * future copy change remains a single edit.
 *
 * API-ready by construction: replacing STATUS_ICON_CATALOG with a fetched response is the only
 * change needed — the render path consumes exactly this shape and drops ids it does not know.
 *
 * Field contract:
 *   id        stable string key, and a PERSISTED DATA CONTRACT: it is written into
 *             tb_conversation.status_icons. Renaming one needs a JSON data migration (SA spec R8).
 *   label     display text (Thai, unconfirmed).
 *   fa        FontAwesome class. FA 6.4 free is already global (main.js); no new SVG was added.
 *   badgeText rendered as a pure-CSS badge INSTEAD of `fa` — FontAwesome has no "OK" glyph.
 *   color     glyph ink.
 *   emoji     text stand-in for the glyph, used when the name is copied (PanelHeaderV2026).
 *   order     explicit sort, so a future API can reorder without relying on array order.
 *   repeat    draw `fa` this many times (default 1) — the งานด่วน levels are 1, 2 and 3 flames.
 *   group     ids sharing a group are ONE choice: ticking one unticks the rest (the urgency level).
 *             The server enforces it too (URGENT_LEVEL_IDS in statusIconHelper.js keeps the highest).
 *   rails     customer_type list this status can be PICKED on (e.g. ['customer'], ['beneat_professional']).
 *             Omitted = every rail that has the picker (STATUS_ICON_CUSTOMER_TYPES). Only the picker
 *             filters by it — cards still render any saved id. FRONTEND-ONLY by decision: the server
 *             validates ids and the rail gate, not which id belongs to which rail.
 *   watermark true = this status also gets the LARGE card watermark. Only the flame has it; the
 *             mockup annotation is "icon สถานะใหญ่จะขึ้นแค่ไฟเท่านั้น สถานะอื่นไม่แสดง". Encoded as
 *             data rather than an `if (id === 'urgent')` in the renderer.
 */
export const STATUS_ICON_CATALOG = [
  { id: 'package_or_undated_booking', label: 'คุณลูกแพ็คเกจหรือมีการจองไม่ระบุวัน', fa: 'fas fa-circle', color: '#2F80ED', emoji: '🔵', order: 1, rails: ['customer'] },
  { id: 'coordinating', label: 'กำลังประสานงานหรือเช็คคิวผู้ให้บริการ', fa: 'fas fa-square-check', color: '#27AE60', emoji: '✅', order: 2, rails: ['customer'] },
  { id: 'awaiting_transfer', label: 'รอโอนเงิน เพื่อทำจอง /เพิ่มชั่วโมง', fa: 'fas fa-circle-minus', color: '#EB5757', emoji: '⛔', order: 3, rails: ['customer'] },
  { id: 'transferred_awaiting_coord', label: 'โอนแล้วรอประสานงานจอง', badgeText: 'OK', color: '#2F80ED', emoji: '🆗', order: 4, rails: ['customer'] },
  { id: 'awaiting_close', label: 'รอปิดเคส', fa: 'fas fa-thumbtack', color: '#E03131', emoji: '📌', order: 5 },
  { id: 'awaiting_refund', label: 'รอโอนคืน', fa: 'fas fa-repeat', color: '#2F80ED', emoji: '🔁', order: 6, rails: ['customer'] },
  { id: 'urgent', label: 'งานด่วน ระดับ 1', fa: 'fas fa-fire', color: '#F2994A', emoji: '🔥', order: 7, watermark: true, group: 'urgent' },
  { id: 'urgent_2', label: 'งานด่วน ระดับ 2', fa: 'fas fa-fire', repeat: 2, color: '#F2994A', emoji: '🔥🔥', order: 8, watermark: true, group: 'urgent' },
  { id: 'urgent_3', label: 'งานด่วน ระดับ 3', fa: 'fas fa-fire', repeat: 3, color: '#F2994A', emoji: '🔥🔥🔥', order: 9, watermark: true, group: 'urgent' }
]

// The id the server ticks by itself when the AI flags the case urgent (is_emergency = 1) — level 1,
// and only when no level is set yet. NOT locked — the admin can untick it or change the level. Mirrors EMERGENCY_STATUS_ICON_ID in statusIconHelper.js.
export const EMERGENCY_STATUS_ICON_ID = 'urgent'

// Catalogue in display order. Sorted once at module load, not per render.
const ORDERED = STATUS_ICON_CATALOG.slice().sort((a, b) => (a.order || 0) - (b.order || 0))

/** Entries pickable on this rail (customer_type), in display order. */
export function statusIconsForRail (customerType) {
  return ORDERED.filter(item => !item.rails || item.rails.indexOf(customerType) !== -1)
}

/**
 * Resolve an array of ids into catalogue entries, in catalogue order.
 * Unknown ids render nothing (they are simply not in the catalogue) — they are NOT dropped from the
 * caller's own array, so a future save cannot destroy another admin's id after a catalogue edit.
 */
export function resolveStatusIcons (ids) {
  if (!Array.isArray(ids) || ids.length === 0) return []
  return ORDERED.filter(item => ids.indexOf(item.id) !== -1)
}

// Leading run of status emoji (each optionally followed by U+FE0F), with spaces between them.
const LEADING_STATUS_EMOJI = new RegExp(
  '^\\s*(?:(?:' + ORDERED.filter(item => item.emoji).map(item => item.emoji).join('|') + ')\\uFE0F?\\s*)+',
  'u'
)

/**
 * Drops the status emoji the copy-name button puts before a name, so a pasted name still matches
 * tb_customer.name. Only the leading run: emoji inside the real name stay untouched.
 */
export function stripLeadingStatusEmoji (text) {
  return typeof text === 'string' ? text.replace(LEADING_STATUS_EMOJI, '') : text
}

/** TRUE when the selection contains a status flagged `watermark` (today: the flame only). */
export function hasWatermarkStatus (ids) {
  return resolveStatusIcons(ids).some(item => item.watermark === true)
}
